// src/lib/kinema/core/Editor.ts
import type {
  EditorState, EditorConfig, Command, CommandListener,
  WildcardListener, Middleware, InverseOperation, Unsubscribe,
  LayerTypeDefinition, Interpolator, StateListener,
} from './types';
import { WILDCARD, getCompositionProps } from './types';
import { createEditorState } from './EditorState';
import { CommandRegistry } from './commands/CommandRegistry';
import { runMiddleware } from './commands/middleware';
import { LayerTypeRegistry } from './layers/LayerTypeRegistry';
import { builtInLayerTypes } from './layers/builtInTypes';
import { InterpolatorRegistry } from './effects/InterpolatorRegistry';
import { History } from './history/History';
import { snapToFrame } from './frameUtils';
import {
  ADD_LAYER, REMOVE_LAYER, UPDATE_PROPERTY, MOVE_LAYER,
  SELECT_LAYER, SET_PLAYHEAD,
  ADD_EFFECT, ADD_KEYFRAME, UPDATE_KEYFRAME, REMOVE_KEYFRAME,
  REMOVE_EFFECT, TOGGLE_EFFECT,
} from './commands/commands';
import { handleAddLayer, handleRemoveLayer, handleUpdateProperty, handleMoveLayer } from './handlers/layerHandlers';
import { handleSelectLayer } from './handlers/selectionHandlers';
import { handleSetPlayhead } from './handlers/playbackHandlers';
import {
  handleAddEffect, handleAddKeyframe, handleUpdateKeyframe,
  handleRemoveKeyframe, handleRemoveEffect, handleToggleEffect,
} from './handlers/effectHandlers';

export class Editor {
  private state: EditorState;
  private commandRegistry = new CommandRegistry();
  private layerTypeRegistry = new LayerTypeRegistry();
  private interpolatorRegistry = new InterpolatorRegistry();
  private history = new History();
  private middlewares: Middleware[] = [];
  private stateListeners = new Set<StateListener>();
  private onError?: (error: Error) => void;
  private batching = false;
  private batchInverses: InverseOperation[] = [];

  // Playback fields
  private animationFrameId: number | null = null;
  private lastFrameTime: number | null = null;
  /**
   * When set, Editor.tick reads playhead time from this function instead of
   * advancing rawPlayheadTime by wall-clock delta. Lets a media element
   * (audio/video) be the authoritative clock during play, eliminating drift
   * between the editor and the element's internal clock.
   */
  private externalClockSource: (() => number) | null = null;

  constructor(config?: EditorConfig) {
    this.state = createEditorState({
      canvas: config?.canvas,
      frameRate: config?.frameRate,
    });
    this.onError = config?.onError;
    this.middlewares = config?.middleware ? [...config.middleware] : [];

    // Register built-in layer types
    for (const type of builtInLayerTypes) {
      this.layerTypeRegistry.register(type);
    }
    // Register additional layer types from config
    if (config?.layerTypes) {
      for (const type of config.layerTypes) {
        this.layerTypeRegistry.register(type);
      }
    }

    this.registerBuiltInHandlers();
  }

  private registerBuiltInHandlers(): void {
    const reg = this.layerTypeRegistry;

    this.commandRegistry.registerHandler(ADD_LAYER, (state, payload) =>
      handleAddLayer(state, payload, reg),
    );
    this.commandRegistry.registerHandler(REMOVE_LAYER, (state, payload) =>
      handleRemoveLayer(state, payload),
    );
    this.commandRegistry.registerHandler(UPDATE_PROPERTY, (state, payload) =>
      handleUpdateProperty(state, payload),
    );
    this.commandRegistry.registerHandler(MOVE_LAYER, (state, payload) =>
      handleMoveLayer(state, payload),
    );
    this.commandRegistry.registerHandler(SELECT_LAYER, (state, payload) =>
      handleSelectLayer(state, payload),
    );
    this.commandRegistry.registerHandler(SET_PLAYHEAD, (state, payload) =>
      handleSetPlayhead(state, payload),
    );
    this.commandRegistry.registerHandler(ADD_EFFECT, (state, payload) =>
      handleAddEffect(state, payload),
    );
    this.commandRegistry.registerHandler(ADD_KEYFRAME, (state, payload) =>
      handleAddKeyframe(state, payload),
    );
    this.commandRegistry.registerHandler(UPDATE_KEYFRAME, (state, payload) =>
      handleUpdateKeyframe(state, payload),
    );
    this.commandRegistry.registerHandler(REMOVE_KEYFRAME, (state, payload) =>
      handleRemoveKeyframe(state, payload),
    );
    this.commandRegistry.registerHandler(REMOVE_EFFECT, (state, payload) =>
      handleRemoveEffect(state, payload),
    );
    this.commandRegistry.registerHandler(TOGGLE_EFFECT, (state, payload) =>
      handleToggleEffect(state, payload),
    );
  }

  getState(): EditorState {
    return this.state;
  }

  dispatchCommand<T>(command: Command<T>, payload: T): void {
    try {
      // Run middleware
      const processedPayload = runMiddleware(command, payload, this.middlewares, this.state);
      if (processedPayload === null) return; // blocked

      // Execute handler
      const result = this.commandRegistry.execute(command, processedPayload, this.state);
      this.state = result.state;

      // Push to history
      if (result.inverse) {
        if (this.batching) {
          this.batchInverses.push(result.inverse);
        } else {
          this.history.pushUndo([result.inverse]);
        }
      }

      // Notify state listeners (unless batching)
      if (!this.batching) {
        this.notifyStateListeners();
      }
    } catch (error) {
      if (this.onError) {
        this.onError(error instanceof Error ? error : new Error(String(error)));
      } else {
        throw error;
      }
    }
  }

  batch(fn: () => void): void {
    this.batching = true;
    this.batchInverses = [];
    try {
      fn();
      if (this.batchInverses.length > 0) {
        this.history.pushUndo(this.batchInverses);
      }
    } finally {
      this.batching = false;
      this.batchInverses = [];
      this.notifyStateListeners();
    }
  }

  undo(): void {
    const inverses = this.history.popUndo();
    if (!inverses) return;
    // Execute inverses in reverse order, collect new inverses for redo
    const redoOps: InverseOperation[] = [];
    for (const op of [...inverses].reverse()) {
      const result = this.commandRegistry.execute(op.command, op.payload, this.state);
      this.state = result.state;
      if (result.inverse) redoOps.push(result.inverse);
    }
    this.history.pushRedo(redoOps);
    this.notifyStateListeners();
  }

  redo(): void {
    const forwards = this.history.popRedo();
    if (!forwards) return;
    // Execute forward ops in reverse order, collect new inverses for undo
    const undoOps: InverseOperation[] = [];
    for (const op of [...forwards].reverse()) {
      const result = this.commandRegistry.execute(op.command, op.payload, this.state);
      this.state = result.state;
      if (result.inverse) undoOps.push(result.inverse);
    }
    // Push to undo stack WITHOUT clearing redo
    this.history.pushUndo(undoOps, false);
    this.notifyStateListeners();
  }

  canUndo(): boolean {
    return this.history.canUndo();
  }

  canRedo(): boolean {
    return this.history.canRedo();
  }

  registerCommandListener<T>(
    command: Command<T>,
    listener: CommandListener<T> | WildcardListener,
  ): Unsubscribe {
    if (command.type === WILDCARD.type) {
      return this.commandRegistry.registerWildcardListener(listener as WildcardListener);
    }
    return this.commandRegistry.registerListener(command, listener as CommandListener<T>);
  }

  registerMiddleware(middleware: Middleware): void {
    this.middlewares.push(middleware);
  }

  registerLayerType(definition: LayerTypeDefinition): void {
    this.layerTypeRegistry.register(definition);
  }

  registerInterpolator(name: string, interpolator: Interpolator): void {
    this.interpolatorRegistry.register(name, interpolator);
  }

  subscribe(listener: StateListener): Unsubscribe {
    this.stateListeners.add(listener);
    return () => { this.stateListeners.delete(listener); };
  }

  private notifyStateListeners(): void {
    this.stateListeners.forEach((listener) => listener(this.state));
  }

  // ─── Serialization ────────────────────────────────────
  toJSON(): any {
    return {
      layers: Object.fromEntries(
        Object.entries(this.state.layers).map(([id, layer]) => [
          id,
          { ...layer, effects: { ...layer.effects } },
        ]),
      ),
      rootLayerId: this.state.rootLayerId,
      playheadTime: this.state.playheadTime,
    };
  }

  loadState(data: any): void {
    // If saved data has a rootLayerId, use it. Otherwise create a root comp.
    if (data.rootLayerId && data.layers?.[data.rootLayerId]) {
      this.state = {
        layers: data.layers,
        rootLayerId: data.rootLayerId,
        selectedIds: new Set(),
        primarySelectedId: null,
        playheadTime: data.playheadTime ?? 0,
        isPlaying: false,
      };
    } else {
      // Legacy format — create root comp from top-level fields
      const newState = createEditorState({
        canvas: data.canvas ?? { width: 1920, height: 1080 },
        duration: data.duration ?? 10,
        frameRate: data.frameRate ?? 24,
      });
      // Import layers, parenting them to the new root
      for (const [id, layer] of Object.entries(data.layers ?? {})) {
        const l = layer as any;
        newState.layers[id] = { ...l, parentId: l.parentId ?? newState.rootLayerId };
      }
      newState.playheadTime = data.playheadTime ?? 0;
      this.state = newState;
    }
    this.history.clear();
    this.notifyStateListeners();
  }

  // ─── Playback ─────────────────────────────────────────
  play(): void {
    if (this.state.isPlaying) return;
    // If at the end, restart from beginning
    const { duration } = getCompositionProps(this.state);
    if (this.state.playheadTime >= duration && duration > 0) {
      this.state = { ...this.state, playheadTime: 0 };
    }
    this.rawPlayheadTime = this.state.playheadTime;
    this.state = { ...this.state, isPlaying: true };
    this.lastFrameTime = performance.now();
    this.animationFrameId = requestAnimationFrame(this.tick);
    this.notifyStateListeners();
  }

  pause(): void {
    if (!this.state.isPlaying) return;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.state = { ...this.state, isPlaying: false };
    this.lastFrameTime = null;
    this.notifyStateListeners();
  }

  seek(time: number): void {
    const { frameRate } = getCompositionProps(this.state);
    const snapped = snapToFrame(time, frameRate);
    this.dispatchCommand(SET_PLAYHEAD, { time: snapped });
  }

  setDuration(duration: number): void {
    this.dispatchCommand(UPDATE_PROPERTY, {
      layerId: this.state.rootLayerId,
      property: 'duration',
      value: duration,
    });
  }

  private rawPlayheadTime = 0;

  setExternalClockSource(fn: (() => number) | null): void {
    this.externalClockSource = fn;
    if (fn) {
      // When switching to an external clock, sync raw to current element time
      // so we don't snap-jump on the next tick.
      this.rawPlayheadTime = fn();
    }
  }

  private tick = (): void => {
    if (!this.state.isPlaying) return;
    const now = performance.now();
    const delta = (now - (this.lastFrameTime ?? now)) / 1000;
    this.lastFrameTime = now;

    const { frameRate, duration } = getCompositionProps(this.state);

    if (this.externalClockSource) {
      this.rawPlayheadTime = this.externalClockSource();
    } else {
      this.rawPlayheadTime += delta;
    }
    const snappedTime = snapToFrame(this.rawPlayheadTime, frameRate);

    if (snappedTime >= duration && duration > 0) {
      this.rawPlayheadTime = duration;
      this.state = { ...this.state, playheadTime: duration, isPlaying: false };
      this.notifyStateListeners();
      return;
    }

    // Only update state when we've crossed a frame boundary
    if (snappedTime !== this.state.playheadTime) {
      this.state = { ...this.state, playheadTime: snappedTime };
      this.notifyStateListeners();
    }

    this.animationFrameId = requestAnimationFrame(this.tick);
  };

  // ─── Accessors ────────────────────────────────────────
  getLayerTypeRegistry(): LayerTypeRegistry {
    return this.layerTypeRegistry;
  }

  getInterpolatorRegistry(): InterpolatorRegistry {
    return this.interpolatorRegistry;
  }
}
